import * as React from 'react';
import * as R from 'ramda';
import ReportRecipientLayout from './ReportRecipientLayout';
import ReportRecipientTable from './ReportRecipientTable';
import LocalDiv from '../localization/LocalDiv';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { css } from '@emotion/core';
import { FLEX_ROW, FLEX_COLUMN } from '../layout/flexBox';
import LocalBtn from '../localization/LocalBtn';
import { btnStyle } from '../forms/styles/button';
import { COLORS } from '../globalStyle/colors';
import Spacer from '../layout/Spacer';
import { useAppState } from '../core/redux/hooks';
import SendIcon from '../icons/SendIcon';
import DownloadIcon from '../icons/DownloadIcon';
import { useCurrentUser } from '../auth/hooks';
import { ReportRecipientReport, ReportDeliveryRequest } from './models';
import { useDispatch } from 'react-redux';
import { deliverReportsAction } from './api/deliverReportsAction';
import { deliverReportsToRecipientAction } from  './api/deliverReportsToRecipientAction';
import { useMemo, useState } from 'react';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import { useTimedToggle } from '../core/hooks/useTimedToggle';
import { Endpoints } from '../core/api/endpoints';
import { isNilOrEmpty } from '../language/module';
import DeliverReportsModal from './DeliverReportsModal';
import { useToggle } from '../core/hooks/useToggle';


const styles = {
  header: css`
    margin: 20px auto 40px;
    font-size: 26px;
    
    ${onMobileDevice} {
      padding-top: 25px;
    }
  `,
  btnRow: {
    root:        [
      FLEX_ROW,
      css`
        justify-content: flex-end;
        margin-top: 12px;
      `,
    ],
    deliverBtn:  btnStyle.transparent(COLORS.BLUE),
    downloadBtn: btnStyle.primary(COLORS.BLUE),
  },
  icon: css`
    padding-top: 10px;
  `,
  downloadError: css`
    align-self: center;
    margin-right: 12px;
    color: ${COLORS.RED};
  `,
};


type Props = {
  className?: string;
}


/**
 * ReportRecipientDashboard: a component that enables report recipients to handle reports.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportRecipientDashboard: React.FC<Props> = props => {
  const dir     = useAppState(state => state.locale.textDirection);

  const currentUser  = useCurrentUser();
  const hasDeliverPermission = useMemo(() => currentUser?.hasReportRecipientDeliverPermission || false, [currentUser]);
  const hasDeliverThirdPartyPermission = useMemo(
    () => currentUser?.hasReportRecipientDeliverThirdPartyPermission || false, [currentUser],
  ) 
  const recipientId  = useMemo(() => currentUser?.personId ?? -1, [currentUser]);
  const userName  = useMemo(() => currentUser?.userName || '', [currentUser]);

  const [delivered, setDelivered] = useState<number>(0);
  const [downloaded, setDownloaded] = useState<number>(0);

  const [messageVisible, showMessage] = useTimedToggle(false, 5000);
  const [delivering, toggleDeliverModal] = useToggle();
  
  const languageId = useAppState(state => state.locale.lang.languageId);

  const dispatch = useDispatch();
  
  const deliverClicked = (report: ReportRecipientReport) => {
    const request = new Array<ReportDeliveryRequest>();
    request.push(
      {
        projectId: report.projectId,
        reportSetId: report.reportSetId,
        participantId: report.participantId,
        archiveDir: report.archiveDir,
        languageId: report.languageId,
        firstName: report.firstName,
        lastName: report.lastName,
        reportId: report.reportId,
        participantReportId: report.participantReportId,
      },
    );
    dispatch(deliverReportsAction(request, languageId));
    setDownloaded(0);
    setDelivered(1);
    showMessage();
  };

  const deliverClickedTo = (
    reports: ReportRecipientReport[], 
    toParticipant: boolean, 
    firstName: string, 
    lastName: string, 
    email: string,    
  ) => {
    if (toParticipant || isNilOrEmpty(email)) {
      if (reports.length > 1) {
        deliverSelectedClicked(reports, true, '', '','');
      } else {
        deliverClicked(reports[0]);        
      }
    } else {
      deliverSelectedClicked(reports, false, firstName, lastName, email);
    }
  };

  const downloadClicked = () => {
    setDelivered(0);
    setDownloaded(1);
    showMessage();
  };

  const reports = useAppState(state => state.reportRecipientReports || []);

  const selected = useMemo(
    () => reports.filter(r => r.isChecked),
    [reports],
  );

  const downloadAllCount = useMemo(
    () => reports.filter(report => report.fileExists).length,
    [reports],
  );   

  const downloadSelectedCount = useMemo(
    () => selected.filter(report => report.fileExists).length,
    [selected],
  );

  const deliverRequest = useMemo(
    () => selected.map(report => ({ 
      projectId: report.projectId,
      reportSetId: report.reportSetId,
      participantId: report.participantId,
      archiveDir: report.archiveDir,
      languageId: report.languageId,
      firstName: report.firstName,
      lastName: report.lastName,
      reportId: report.reportId,
      participantReportId: report.participantReportId,
    })),
    [selected],
  );
  
  const deliverSelectedClicked = (
      reports: ReportRecipientReport[],
      toParticipant: boolean, 
      firstName: string, 
      lastName: string, 
      email: string,
    ) => {
    const selectedRequest = reports.map(report => ({ 
      projectId: report.projectId,
      reportSetId: report.reportSetId,
      participantId: report.participantId,
      archiveDir: report.archiveDir,
      languageId: report.languageId,
      firstName: report.firstName,
      lastName: report.lastName,
      reportId: report.reportId,
      participantReportId: report.participantReportId,
    }),
      reports,
    );
    if (toParticipant || isNilOrEmpty(email) ) {
      dispatch(deliverReportsAction(selectedRequest, languageId));
    } else {
      dispatch(deliverReportsToRecipientAction(selectedRequest, email, firstName, lastName, languageId));
    }
    setDownloaded(0);
    setDelivered(selectedRequest.length);
    showMessage();
  };

  const deliverSelectedDirectClicked = () => {
    const selectedRequest = deliverRequest;
    dispatch(deliverReportsAction(deliverRequest, languageId));
    setDownloaded(0);
    setDelivered(selectedRequest.length);
    showMessage();
  }

  const allSelected = R.all(r => r.isChecked, reports) && reports.length > 1;

  const last = useAppState(state => state.reportRecipientLastOnly);
  const dates = useAppState(state => state.reportRecipientDateRange || 3);
  const projects = useAppState(state => state.reportRecipientSelectedProjects || {});
  const participants = useAppState(state => state.reportRecipientSelectedParticipants || {});

  const deliverDisabled = useMemo(
    () => allSelected
      ? reports.length === 0
      : selected.length === 0 && reports.length > 1,
    [allSelected, reports, selected],
  );

  const downloadDisabled = useMemo(
    () => allSelected
    ? reports.filter(r => r.fileExists).length === 0
    : selected.filter(r => r.fileExists).length === 0 && reports.filter(r => r.fileExists).length > 1,
  [allSelected, reports, selected],
  );

  const [downloading, setDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<boolean>(false);

  // Pull the server-supplied file name out of the Content-Disposition header, falling back to a default.
  const filenameFromResponse = (response: Response): string => {
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    return match && match[1] ? decodeURIComponent(match[1]) : 'Reports.zip';
  };

  // Request a zip via POST (the criteria travel in the body, not the URL, so there is no length limit)
  // and trigger the browser download from the returned blob.
  const postDownloadRequest = async (endpoint: string, body: string, count: number) => {
    setDownloadError(false);
    setDownloading(true);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
      });

      if (!response.ok) {
        setDownloadError(true);
        return;
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = filenameFromResponse(response);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);

      setDelivered(0);
      setDownloaded(count);
      showMessage();
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  };

  const downloadAllBody = () => JSON.stringify({
    recipientId,
    dateRange: dates,
    projectIds: Object.values(projects || {}).map(a => a.projectId),
    participantIds: Object.values(participants || {}).map(a => a.participantId),
    lastOnly: last,
  });

  const downloadReports = async () => {
    if (downloadDisabled || downloading || selected.length === 0) {
      return;
    }

    if (allSelected) {
      await postDownloadRequest(Endpoints.DownloadAllReports, downloadAllBody(), downloadAllCount);
    } else {
      await postDownloadRequest(
        Endpoints.DownloadSelectedReports,
        JSON.stringify(selected.map(r => r.fileOnly)),
        downloadSelectedCount,
      );
    }
  };

  // The "All" link in the report list column header: downloads every report matching the current
  // filters, regardless of which rows are checked.
  const downloadAllClicked = () => {
    if (downloading) {
      return;
    }

    postDownloadRequest(Endpoints.DownloadAllReports, downloadAllBody(), downloadAllCount);
  };

  return (
    <ReportRecipientLayout>

      <LocalDiv css={styles.header}>
        <Msg str={Str.ReportPortal} />
      </LocalDiv>

      <ReportRecipientTable
        textDirection={dir}
        recipientId={recipientId}
        userName={userName}
        hasDeliverPermission={hasDeliverPermission}
        hasDeliverThirdPartyPermission={hasDeliverThirdPartyPermission}
        deliverClicked={deliverClicked}
        deliverClickedTo={deliverClickedTo}
        redeliverClicked={deliverClicked}
        downloadClicked={downloadClicked}
        downloadAllClicked={downloadAllClicked}
        delivered={delivered}
        downloaded={downloaded}
        messageVisible={messageVisible}
      />

      <LocalDiv css={styles.btnRow.root}>
        {
          hasDeliverPermission && (
            hasDeliverThirdPartyPermission
            ? <LocalBtn 
                str={allSelected ? Str.DeliverAll : Str.DeliverSelected}
                svg={<SendIcon sizePx={14} fill={COLORS.BLUE} />}
                css={styles.btnRow.deliverBtn}
                onClick={toggleDeliverModal}
                disabled={deliverDisabled}
              />
            : <LocalBtn
                str={allSelected ? Str.DeliverAll : Str.DeliverSelected}
                svg={<SendIcon sizePx={14} fill={COLORS.BLUE} />}
                css={styles.btnRow.deliverBtn}
                onClick={deliverSelectedDirectClicked}
                disabled={deliverDisabled}
              />
          )
        }
        <Spacer sizePx={10} />
        {downloadError && (
          <Msg str={Str.DownloadFailed} css={styles.downloadError} />
        )}
        <button
          type='button'
          css={[FLEX_ROW, styles.btnRow.downloadBtn]}
          onClick={downloadReports}
          disabled={downloadDisabled || downloading}
        >
          <DownloadIcon sizePx={14} fill={COLORS.WHITE} css={[FLEX_COLUMN, styles.icon]}/>
          <Spacer sizePx={8} css={FLEX_COLUMN} />
          <Msg str={allSelected ? Str.DownloadAll : Str.DownloadSelected} css={FLEX_COLUMN} />
        </button>
        {
        delivering && (
          <DeliverReportsModal
            reports={(selected && selected.length > 0 ? selected : (reports.length === 1 ? reports : [])) }
            deliverReport={deliverClickedTo}
            dismiss={toggleDeliverModal}
          />
        )
      }      </LocalDiv>
    </ReportRecipientLayout>
  );
};

export default ReportRecipientDashboard;
